// 스테이지 지도 표면 재질(S2·S6, TSL, WebGPU·WebGL2 공용): 바닥(지도별 판석·흙·뿌리·현무암 조약돌), 절벽 단면(지역2 현무암 기둥), 수면, 용암 바다, 용암 폭포, 룬 고리.
// 외곽선 없이 손그림 색면(ART-BIBLE 3장). 모든 잡음은 월드 좌표라 이음매가 없고 추가 텍스처 요청도 없다(바닥 판석 1장 + 가장자리 질감 1장).
import * as THREE from 'three/webgpu';
import {
  abs, atan, dFdx, dFdy, float, floor, fract, length, luminance, max, min, mix, mx_noise_float, mx_worley_noise_vec2, positionView, positionWorld, sin, smoothstep, step, texture, time, uv, vec2, vec3,
} from 'three/tsl';
import { tiled } from './surfaces';

export type { StageLookKey } from './stageSets';
/** 지역1 표면 규칙 키 */
type R1Look = 'a' | 'b' | 'm' | 'f';
type N = THREE.Node<'float'>;
type V3 = THREE.Node<'vec3'>;

/** 지도별 바닥 규칙 */
interface FloorRule {
  /** 판석 한 장 크기(m) */
  tile: number;
  /** 가장자리 이끼 색·세기 */
  moss: readonly [number, number, number];
  mossK: number;
  /** 잎 그늘 얼룩 세기(0 = 흐림·실내) */
  dapple: number;
  /** 바닥 최종 곱 */
  tint: readonly [number, number, number];
  /** 채도(1 = 원본) */
  sat: number;
  /** 가장자리 젖음(어둡고 차갑게) 세기 */
  wet: number;
  /** 바닥 전체 밝기 곱 */
  gain: number;
}
const FLOOR_RULE: Readonly<Record<R1Look, FloorRule>> = {
  a: { tile: 5.25, moss: [0.3, 0.42, 0.18], mossK: 0.5, dapple: 1, tint: [1.0, 0.98, 0.92], sat: 0.86, wet: 0, gain: 1.06 },
  b: { tile: 3.4, moss: [0.24, 0.4, 0.28], mossK: 0.32, dapple: 0.5, tint: [0.9, 0.96, 1.02], sat: 0.7, wet: 0.5, gain: 0.7 },
  m: { tile: 6.5, moss: [0.34, 0.36, 0.16], mossK: 0.14, dapple: 0.6, tint: [1.02, 0.97, 0.9], sat: 0.85, wet: 0, gain: 1.0 },
  f: { tile: 7.5, moss: [0.26, 0.4, 0.16], mossK: 0.5, dapple: 1, tint: [0.98, 0.99, 0.88], sat: 0.82, wet: 0, gain: 0.95 },
};

export interface ProximityTex {
  /** 칸마다 가장자리 근접도(0..1 → 0..255, 가장자리일수록 큼), 크기 = 판정 격자 */
  readonly tex: THREE.DataTexture;
  readonly originX: number;
  readonly originZ: number;
  readonly width: number;
  readonly height: number;
}

/** 칸 데이터 질감(R8, 선형 보간). 격자 칸 중심이 질감 칸 중심이다 */
export function dataTexture(data: Uint8Array, w: number, h: number): THREE.DataTexture {
  const t = new THREE.DataTexture(data, w, h, THREE.RedFormat, THREE.UnsignedByteType);
  t.magFilter = t.minFilter = THREE.LinearFilter;
  t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
  t.needsUpdate = true;
  return t;
}

/** 바닥 윗면: 판석 + 큰 얼룩 + 잎 그늘 + 가장자리 이끼/젖음. 윗면 한 장(band + interior 합친 메시)에 씌운다 */
export function stageFloorMaterial(tex: THREE.Texture | null, prox: ProximityTex, key: R1Look): THREE.MeshStandardNodeMaterial {
  const rule = FLOOR_RULE[key];
  const m = new THREE.MeshStandardNodeMaterial({ roughness: 0.95, metalness: 0 });
  const wp = positionWorld.xz;
  const t = tex ? tiled(tex, wp, rule.tile, key === 'a' ? 3 : key === 'b' ? 5 : key === 'm' ? 7 : 11) : { col: vec3(0.5, 0.48, 0.42), tint: float(1) };
  const macro = mx_noise_float(vec3(wp.mul(0.06), 1.7)).mul(0.5).add(0.5);
  const soil = smoothstep(0.42, 0.72, mx_noise_float(vec3(wp.mul(0.105), 12.4)).mul(0.5).add(0.5));
  const sUV = vec2(wp.x.sub(prox.originX).div(prox.width), wp.y.sub(prox.originZ).div(prox.height));
  const s = texture(prox.tex, sUV).r as N;
  let col: V3 = t.col.mul(t.tint).mul(mix(float(0.86), float(1.12), macro));
  col = mix(col, col.mul(vec3(0.72, 0.78, 0.66)), soil.mul(0.2));
  // 판석마다 색 차이 + 줄눈은 더 어둡게
  const hue = mx_noise_float(vec3(wp.mul(0.9), 8.1));
  col = col.mul(vec3(float(1).add(hue.mul(0.08)), float(1).add(hue.mul(0.02)), float(1).sub(hue.mul(0.06))));
  col = col.mul(mix(float(0.66), float(1.08), smoothstep(0.16, 0.5, luminance(t.col))));
  // 숲 지붕 사이로 떨어지는 얼룩 햇빛
  const leaf = mx_noise_float(vec3(wp.mul(0.21), 6.2)).mul(0.65).add(mx_noise_float(vec3(wp.mul(0.67), 2.9)).mul(0.35)).mul(0.5).add(0.5);
  const dapple = mix(float(0.74), float(1.14), smoothstep(0.38, 0.62, leaf));
  col = col.mul(mix(float(1), dapple, rule.dapple));
  col = mix(vec3(luminance(col)), col, rule.sat).mul(vec3(rule.tint[0], rule.tint[1], rule.tint[2])).mul(rule.gain);
  // 가장자리: 이끼가 줄눈부터 차오르고(고주파 잡음으로 덩어리), 물가는 젖어 어둡고 차갑다
  const lump = smoothstep(0.35, 0.65, mx_noise_float(vec3(wp.mul(0.75), 3.3)).mul(0.5).add(0.5).add(s.mul(0.35)));
  const mossCol: V3 = vec3(rule.moss[0], rule.moss[1], rule.moss[2]).mul(luminance(col).mul(1.5).add(0.12));
  col = mix(col, mossCol, s.mul(lump).mul(rule.mossK).mul(1.5).clamp(0, 0.92));
  if (rule.wet > 0) col = mix(col, col.mul(vec3(0.62, 0.76, 0.8)), smoothstep(0.35, 1.0, s).mul(rule.wet));
  // 큰 가장자리 그늘(절벽 끝이 더 어둡지 않게 아주 옅게)
  m.colorNode = col.mul(float(1).sub(s.mul(s).mul(0.08)));
  return m;
}

/** 절벽·둑 단면 규칙 */
interface CliffRule {
  light: readonly [number, number, number];
  dark: readonly [number, number, number];
  moss: readonly [number, number, number];
  mossK: number;
}
const CLIFF_RULE: Readonly<Record<R1Look, CliffRule>> = {
  a: { light: [0.56, 0.5, 0.4], dark: [0.26, 0.23, 0.19], moss: [0.28, 0.4, 0.18], mossK: 0.9 },
  b: { light: [0.5, 0.52, 0.5], dark: [0.24, 0.27, 0.27], moss: [0.2, 0.36, 0.22], mossK: 0.55 },
  m: { light: [0.5, 0.42, 0.32], dark: [0.24, 0.19, 0.15], moss: [0.3, 0.36, 0.16], mossK: 0.5 },
  f: { light: [0.42, 0.32, 0.22], dark: [0.2, 0.15, 0.1], moss: [0.24, 0.4, 0.14], mossK: 1.1 },
};

/** 절벽·둑 단면: 층리 + 윗가장자리 이끼 + 세로 물때, 깊어질수록 안개색으로 사라진다. depth = 이 메시의 가장 깊은 y(음수) */
export function stageCliffMaterial(key: R1Look, haze: THREE.Color, depth: number): THREE.MeshStandardNodeMaterial {
  const rule = CLIFF_RULE[key];
  const m = new THREE.MeshStandardNodeMaterial({ roughness: 1, metalness: 0, side: THREE.DoubleSide });
  const wp = positionWorld;
  const n1 = mx_noise_float(vec3(wp.x.mul(0.33), wp.y.mul(0.11), wp.z.mul(0.33))).mul(0.5).add(0.5);
  const n2 = mx_noise_float(vec3(wp.x.mul(1.7), wp.y.mul(0.45), wp.z.mul(1.7))).mul(0.5).add(0.5);
  // 층리: 두께가 다른 수평 지층 띠(층마다 명암이 다르고 위쪽 모서리에 밝은 턱) — 매끈한 물결 대신 계단식으로
  const lvl = wp.y.mul(0.85).add(n1.mul(1.7)).add(n2.mul(0.25));
  const layer = floor(lvl), inLayer = fract(lvl);
  const layerTone = fract(sin(layer.mul(12.9898).add(layer.mul(layer).mul(0.37))).mul(43758.5453));
  const strata = layerTone.mul(0.75).add(inLayer.mul(0.25));
  const along = wp.x.add(wp.z.mul(0.83));
  const streak = smoothstep(0.55, 0.8, mx_noise_float(vec3(along.mul(2.1), wp.y.mul(0.08), 3.7)).mul(0.5).add(0.5));
  // 세로 균열(어두운 가는 선)과 층 경계의 밝은 턱, 철분 얼룩
  const crack = float(1).sub(smoothstep(0.015, 0.06, abs(mx_noise_float(vec3(along.mul(1.25), wp.y.mul(0.16), 8.3)))));
  const ledge = smoothstep(0.84, 0.97, inLayer).mul(0.8).add(float(1).sub(smoothstep(0.0, 0.05, inLayer)).mul(0.5));
  const stain = smoothstep(0.62, 0.82, mx_noise_float(vec3(along.mul(0.45), wp.y.mul(0.5), 12.1)).mul(0.5).add(0.5));
  let col: V3 = mix(vec3(rule.dark[0], rule.dark[1], rule.dark[2]), vec3(rule.light[0], rule.light[1], rule.light[2]), strata.mul(0.55).add(n2.mul(0.45)));
  col = col.mul(float(1).sub(streak.mul(0.3))).mul(float(1).sub(crack.mul(0.5))).add(vec3(rule.light[0], rule.light[1], rule.light[2]).mul(ledge.mul(0.16)));
  col = mix(col, col.mul(vec3(1.18, 0.92, 0.72)), stain.mul(0.5));
  // 윗가장자리 2.5m는 이끼·덩굴이 덮는다(덩어리 잡음)
  const topMask = smoothstep(-2.8, -0.2, wp.y).mul(smoothstep(0.32, 0.62, n1.add(n2.mul(0.3))));
  col = mix(col, vec3(rule.moss[0], rule.moss[1], rule.moss[2]).mul(luminance(col).mul(1.4).add(0.18)), topMask.mul(rule.mossK).clamp(0, 0.95));
  // 깊이가 깊을수록 어두워지다(접지·그늘) 아주 깊은 곳은 안개색으로 사라진다
  col = col.mul(float(0.5).add(smoothstep(-16, -0.6, wp.y).mul(0.5)));
  const fall = float(1).sub(smoothstep(depth, -6, wp.y));
  const fade = fall.mul(fall).mul(0.92).add(fall.mul(0.08));
  m.colorNode = col.mul(float(1).sub(fade));
  m.emissiveNode = vec3(haze.r, haze.g, haze.b).mul(fade).mul(0.9);
  return m;
}

/** 수면(B): 얕은 가장자리(청록, 밝음)에서 깊은 곳(짙은 청녹)으로, 멀수록 하늘빛 반사, 물가 거품 띠, 느린 잔물결 빛. 투명 없이 한 장 */
export function stageWaterMaterial(shore: ProximityTex, haze: THREE.Color): THREE.MeshStandardNodeMaterial {
  const m = new THREE.MeshStandardNodeMaterial({ roughness: 0.18, metalness: 0 });
  const wp = positionWorld.xz;
  const sUV = vec2(wp.x.sub(shore.originX).div(shore.width), wp.y.sub(shore.originZ).div(shore.height));
  const d = texture(shore.tex, sUV).r as N; // 바닥에서 먼 정도 0..1(8m)
  const t = time;
  const w1 = mx_noise_float(vec3(wp.mul(0.55), t.mul(0.22))).mul(0.5).add(0.5);
  const w2 = mx_noise_float(vec3(wp.mul(1.6).add(vec2(t.mul(0.35), t.mul(-0.2))), 4.1)).mul(0.5).add(0.5);
  const w3 = mx_noise_float(vec3(wp.mul(4.2).add(vec2(t.mul(-0.5), t.mul(0.3))), 9.7)).mul(0.5).add(0.5);
  const shallow = vec3(0.2, 0.56, 0.55), mid = vec3(0.05, 0.3, 0.38), deep = vec3(0.015, 0.12, 0.2);
  // 깊이: 바닥에서 1.5m 안쪽은 얕은 청록, 그 밖은 빠르게 짙어진다
  let col: V3 = mix(mix(shallow, mid, smoothstep(0.0, 0.22, d)), deep, smoothstep(0.18, 0.7, d));
  col = col.mul(float(0.85).add(w1.mul(0.3)));
  // 멀수록(스치는 각도) 하늘빛이 비친다
  const far = smoothstep(14.0, 70.0, positionView.z.negate());
  col = mix(col, vec3(haze.r, haze.g, haze.b).mul(vec3(0.86, 0.95, 1.0)), far.mul(0.55));
  // 잔물결 하이라이트: 가는 밝은 선 두 겹
  const glint = smoothstep(0.7, 0.78, w2.mul(0.6).add(w1.mul(0.4))).add(smoothstep(0.78, 0.84, w3.mul(0.7).add(w2.mul(0.3))).mul(0.6));
  // 물가 거품: 바닥 가장자리 0.5m 안에서 느리게 밀려오고 물러난다
  const foamBand = float(1).sub(smoothstep(0.015, 0.07, d));
  const foam = foamBand.mul(float(0.55).add(sin(t.mul(1.3).add(d.mul(60)).add(w1.mul(5))).mul(0.25)).clamp(0, 1)).mul(smoothstep(0.35, 0.6, w2.add(0.15)));
  m.colorNode = mix(col, vec3(0.92, 0.97, 0.96), foam.mul(0.85));
  m.emissiveNode = vec3(0.55, 0.8, 0.82).mul(glint.mul(0.2)).add(vec3(haze.r, haze.g, haze.b).mul(0.05)).add(col.mul(0.12));
  return m;
}

/** 아레나 바닥 상감(A·B): 겹 고리 + 여덟 방위 살 + 가운데 작은 고리 — 판석 위에 깔리는 어두운 돌선이라 플레이를 가리지 않는다 */
export function inlayMedallionMesh(cx: number, cz: number, r: number, tone: 'ruin' | 'quay' | 'ember'): THREE.Mesh {
  const geo = new THREE.CircleGeometry(r + 0.4, 96);
  geo.rotateX(-Math.PI / 2);
  geo.translate(cx, 0, cz);
  const m = new THREE.MeshStandardNodeMaterial({ roughness: 0.9, metalness: 0, transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
  const rel = positionWorld.xz.sub(vec2(cx, cz));
  const rad = length(rel);
  const ang = atan(rel.y, rel.x).div(Math.PI * 2).add(0.5);
  const band = (rr: number, w: number): N => float(1).sub(smoothstep(w * 0.5, w, abs(rad.sub(rr)))) as N;
  const rings = max(max(band(r, 0.3), band(r - 0.8, 0.12)), max(band(r * 0.6, 0.22), band(r * 0.28, 0.16)));
  const tt = fract(ang.mul(8));
  const across = min(tt, float(1).sub(tt)).mul(Math.PI * 2 / 8).mul(rad);
  const spokes = float(1).sub(smoothstep(0.08, 0.16, across)).mul(smoothstep(r * 0.28, r * 0.28 + 0.4, rad)).mul(float(1).sub(smoothstep(r - 0.9, r - 0.6, rad)));
  const dot = float(1).sub(smoothstep(0.35, 0.6, rad));
  const a = max(max(rings, spokes.mul(0.85)), dot).clamp(0, 1);
  const ink = tone === 'ruin' ? vec3(0.17, 0.2, 0.15) : tone === 'ember' ? vec3(0.06, 0.05, 0.06) : vec3(0.43, 0.38, 0.3);
  m.colorNode = ink;
  m.emissiveNode = (tone === 'ruin' ? vec3(0.05, 0.3, 0.3) : tone === 'ember' ? vec3(0.7, 0.18, 0.02) : vec3(0.06, 0.18, 0.25)).mul(a.mul(0.5));
  m.opacityNode = a.mul(tone === 'ember' ? 0.6 : 0.82);
  const mesh = new THREE.Mesh(geo, m);
  mesh.position.set(0, 0.035, 0);
  mesh.renderOrder = 1;
  mesh.name = 'arena-inlay';
  mesh.receiveShadow = true;
  return mesh;
}

/** 룬 고리 메시(중심 cx,cz, 반지름 rm ± 폭, 안쪽 글리프 띠). y는 바닥 바로 위. color = 발광색(기본 녹색, 지역2 제단은 주황) */
export function runeRingMesh(cx: number, cz: number, rOuter: number, rInner: number, color: readonly [number, number, number] = [0.12, 0.8, 0.24]): THREE.Mesh {
  const geo = new THREE.RingGeometry(rInner - 0.5, rOuter + 0.5, 160, 1);
  geo.rotateX(-Math.PI / 2);
  geo.translate(cx, 0, cz);
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
  const rel = positionWorld.xz.sub(vec2(cx, cz));
  const rad = length(rel);
  const ang = atan(rel.y, rel.x).div(Math.PI * 2).add(0.5); // 0..1
  // 바깥 띠 두 줄(굵은 선 + 가는 선), 안쪽 띠 한 줄
  const band = (r: N, w: number): N => float(1).sub(smoothstep(w * 0.5, w, abs(rad.sub(r)))) as N;
  const lineOuter = band(float(rOuter) as N, 0.22).add(band(float(rOuter - 0.9) as N, 0.08).mul(0.8));
  const lineInner = band(float(rInner) as N, 0.16);
  // 두 띠 사이를 채우는 룬: 각도를 칸으로 나눠 칸마다 다른 획 무늬(잡음 임계)를 그린다
  const cells = 44;
  const cid = floor(ang.mul(cells));
  const cu = fract(ang.mul(cells)).sub(0.5);
  const cv = rad.sub((rOuter + rInner) / 2).div((rOuter - rInner) / 2 - 0.55);
  const seed = fract(sin(cid.mul(12.9898)).mul(43758.5453));
  const stroke1 = float(1).sub(smoothstep(0.1, 0.19, abs(cu.mul(1.6).sub(cv.mul(seed.sub(0.5)).mul(1.2)))));
  const stroke2 = float(1).sub(smoothstep(0.1, 0.18, abs(cv.sub(cu.mul(seed.sub(0.4)).mul(2.0)).add(seed.sub(0.5).mul(0.4)))));
  const stroke3 = float(1).sub(smoothstep(0.2, 0.3, length(vec2(cu.mul(1.5), cv.mul(0.9)).sub(vec2(seed.sub(0.5).mul(0.5), seed.mul(0.8).sub(0.4))))));
  const selA = float(1).sub(step(0.33, seed)), selB = step(0.33, seed).mul(float(1).sub(step(0.66, seed))), selC = step(0.66, seed);
  const glyph = max(max(stroke1.mul(selA), stroke2.mul(selB)), stroke3.mul(selC))
    .mul(float(1).sub(smoothstep(0.78, 1.0, abs(cv)))).mul(smoothstep(rInner + 0.35, rInner + 0.6, rad)).mul(float(1).sub(smoothstep(rOuter - 0.7, rOuter - 0.45, rad)));
  const breathe = sin(time.mul(0.7)).mul(0.14).add(0.86);
  const a = min(float(1), lineOuter.add(lineInner).add(glyph.mul(0.9))).mul(breathe);
  m.colorNode = vec3(color[0], color[1], color[2]).mul(a.mul(1.0));
  m.opacityNode = a.clamp(0, 1);
  const mesh = new THREE.Mesh(geo, m);
  mesh.position.set(0, 0.04, 0);
  mesh.renderOrder = 2;
  mesh.name = 'rune-ring';
  return mesh;
}

// ═══ 지역2(잿불 협곡, S6) ═══════════════════════════════════════════

/** surfaces.tiled와 같은 반전 타일 표본(이음매 없음)에 알파(줄눈 마스크)까지 돌려준다 */
function tiledWithAlpha(map: THREE.Texture, world: THREE.Node<'vec2'>, size: number, seed: number): { col: V3; tint: N; a: N } {
  const c = world.div(size);
  const id = floor(c);
  const f = fract(c);
  const lu = mix(f.x, float(1).sub(f.x), step(0.25, fract(id.x.mul(0.5))));
  const lv = mix(f.y, float(1).sub(f.y), step(0.25, fract(id.y.mul(0.5))));
  const s = texture(map, vec2(lu, lv)).grad(dFdx(c), dFdy(c));
  const n = mx_noise_float(vec3(c.mul(0.6), seed * 0.37)).mul(0.5).add(0.5);
  return { col: s.rgb as V3, tint: mix(float(0.9), float(1.06), n) as N, a: s.a as N };
}

/** 지역2 표면 규칙: 판석 크기 · 바닥 색 곱 · 균열 열기(가장자리·용암 웅덩이 곁) · 재 얼룩 */
interface EmberFloorRule { tile: number; tint: readonly [number, number, number]; gain: number; heat: number; ash: number; rim: number }
const EMBER_FLOOR: Readonly<Record<'ea' | 'eb' | 'em' | 'ef', EmberFloorRule>> = {
  ea: { tile: 4.6, tint: [1.0, 0.96, 0.94], gain: 0.84, heat: 1.0, ash: 0.3, rim: 1.0 },
  eb: { tile: 4.2, tint: [1.0, 0.95, 0.94], gain: 0.78, heat: 1.1, ash: 0.22, rim: 1.2 },
  em: { tile: 5.5, tint: [1.0, 0.97, 0.96], gain: 0.82, heat: 0.8, ash: 0.12, rim: 0.8 },
  ef: { tile: 4.8, tint: [1.0, 0.93, 0.96], gain: 0.8, heat: 1.0, ash: 0.35, rim: 1.1 },
};

/**
 * 지역2 바닥: 현무암 조약돌(또는 단조장 쇠판) + 줄눈 일부가 용암빛으로 달아오른다(가장자리·용암 웅덩이 가까울수록, 큰 잡음 덩이로 군데군데).
 * 콘셉트 map-r2-lava-canyon의 "검은 판석 사이 주황 균열". lava = 용암 웅덩이 사각형(판정 칸과 같다)
 */
export function stageEmberFloorMaterial(tex: THREE.Texture | null, prox: ProximityTex, key: 'ea' | 'eb' | 'em' | 'ef', lava: readonly { x0: number; z0: number; x1: number; z1: number }[]): THREE.MeshStandardNodeMaterial {
  const rule = EMBER_FLOOR[key];
  const m = new THREE.MeshStandardNodeMaterial({ roughness: 0.92, metalness: 0 });
  const wp = positionWorld.xz;
  const t = tex ? tiledWithAlpha(tex, wp, rule.tile, 5) : { col: vec3(0.3, 0.29, 0.28), tint: float(1), a: float(1) };
  const lum = luminance(t.col);
  const sUV = vec2(wp.x.sub(prox.originX).div(prox.width), wp.y.sub(prox.originZ).div(prox.height));
  const s = texture(prox.tex, sUV).r as N;
  const macro = mx_noise_float(vec3(wp.mul(0.07), 4.2)).mul(0.5).add(0.5);
  // 용암빛 아래 돌이 금빛으로 뜨지 않게 채도를 누른다(검은 현무암 + 주황 균열 대비)
  let col: V3 = t.col.mul(t.tint).mul(mix(float(0.82), float(1.12), macro));
  col = mix(vec3(luminance(col)), col, 0.6).mul(vec3(rule.tint[0], rule.tint[1], rule.tint[2])).mul(rule.gain);
  // 재 얼룩(밝은 회색 가루가 큰 덩이로)과 그을음(가장자리 쪽이 어둡다)
  const ash = smoothstep(0.58, 0.78, mx_noise_float(vec3(wp.mul(0.16), 9.1)).mul(0.5).add(0.5)).mul(rule.ash);
  col = mix(col, vec3(0.5, 0.48, 0.45).mul(lum.add(0.55)), ash);
  col = col.mul(float(1).sub(s.mul(0.22)));
  // 열기: 가장자리(아래 용암에서 올라오는 열) + 용암 웅덩이 곁 + 군데군데 달아오른 덩이
  let pool: N = float(0) as N;
  for (const q of lava) {
    const d = wp.sub(vec2((q.x0 + q.x1) / 2, (q.z0 + q.z1) / 2)).abs().sub(vec2((q.x1 - q.x0) / 2, (q.z1 - q.z0) / 2));
    pool = pool.max(float(1).sub(smoothstep(0, 2.6, length(d.max(vec2(0)))))) as N;
  }
  const hot = smoothstep(0.66, 0.82, mx_noise_float(vec3(wp.mul(0.11), 2.6)).mul(0.5).add(0.5));
  const heat = s.mul(s).mul(0.55).mul(rule.rim).add(pool).add(hot.mul(0.6)).add(0.26).clamp(0, 1).mul(rule.heat);
  // 줄눈만 빛난다: 바닥 텍스처 알파 = 1 − 줄눈(tools/assets/r2sets/textures.py). 돌 자체는 어둡게 남는다
  const seam = float(1).sub(smoothstep(0.25, 0.75, t.a));
  const flick = mx_noise_float(vec3(wp.mul(0.9), time.mul(0.25))).mul(0.25).add(0.85);
  const glow = seam.mul(heat).mul(flick).clamp(0, 1);
  m.colorNode = mix(col, vec3(0.9, 0.3, 0.05), glow.mul(0.7));
  // 용암 반사광: 아래·곁의 용암이 돌 윗면을 주황으로 물들인다(가장자리·웅덩이 곁일수록) — 콘셉트의 따뜻한 바닥빛
  const bounce = col.mul(vec3(0.55, 0.2, 0.06)).mul(s.mul(0.6).add(pool.mul(0.8)).add(0.18));
  m.emissiveNode = vec3(2.0, 0.5, 0.05).mul(glow.mul(1.1)).add(vec3(0.18, 0.045, 0.008).mul(pool.mul(pool))).add(bounce);
  return m;
}

/**
 * 지역2 절벽: 세로 육각 현무암 기둥 면(기둥마다 명암·폭, 어두운 이음매, 드문드문 가로 마디) + 아래로 갈수록 용암빛이 달아오른다.
 * seaY = 용암 바다 높이(절벽이 거기까지 내려간다)
 */
export function stageBasaltCliffMaterial(seaY: number): THREE.MeshStandardNodeMaterial {
  const m = new THREE.MeshStandardNodeMaterial({ roughness: 0.95, metalness: 0, side: THREE.DoubleSide });
  const wp = positionWorld;
  const along = wp.x.add(wp.z.mul(0.83));
  const warp = mx_noise_float(vec3(along.mul(0.35), wp.y.mul(0.05), 7.7)).mul(0.6);
  const colU = along.mul(1.25).add(warp);
  const cid = floor(colU), cu = fract(colU);
  const tone = fract(sin(cid.mul(12.9898)).mul(43758.5453));
  // 기둥 면: 가운데가 밝고(볼록) 이음매가 어둡다
  const facet = float(1).sub(abs(cu.sub(0.5)).mul(2)).pow(0.6);
  const seamV = float(1).sub(smoothstep(0.0, 0.08, min(cu, float(1).sub(cu))));
  // 가로 마디: 기둥마다 다른 높이에 1.6~3m 간격
  const jy = wp.y.mul(float(0.45).add(tone.mul(0.25))).add(tone.mul(5.3));
  const joint = float(1).sub(smoothstep(0.0, 0.05, float(0.5).sub(abs(fract(jy).sub(0.5)))));
  const grain = mx_noise_float(vec3(along.mul(2.3), wp.y.mul(0.6), 3.1)).mul(0.5).add(0.5);
  let col: V3 = mix(vec3(0.035, 0.038, 0.05), vec3(0.15, 0.145, 0.16), tone.mul(0.55).add(facet.mul(0.35)).add(grain.mul(0.1)));
  col = col.mul(float(1).sub(seamV.mul(0.7))).mul(float(1).sub(joint.mul(0.45)));
  // 윗가장자리 0.8m: 재가 앉아 조금 밝다
  col = mix(col, vec3(0.22, 0.2, 0.2), smoothstep(-0.9, -0.1, wp.y).mul(0.4));
  // 아래쪽 열기: 용암 바다에서 5m 위까지 주황으로 달아오르고, 이음매·마디를 따라 더 밝다
  const low = float(1).sub(smoothstep(seaY, seaY + 5.0, wp.y));
  const crack = seamV.max(joint).mul(smoothstep(0.4, 0.75, grain.add(low.mul(0.4))));
  m.colorNode = mix(col, vec3(0.4, 0.09, 0.02), low.mul(0.6));
  m.emissiveNode = vec3(1.4, 0.28, 0.03).mul(low.mul(low).mul(0.8).add(crack.mul(low).mul(1.4)));
  return m;
}

/**
 * 용암 바다: 식은 껍질 판(검붉은 갈색) 사이로 밝게 녹은 줄이 흐른다. 바닥 가까운 곳(shore 0)은 더 밝게 끓고, 멀수록 껍질이 덮는다.
 * flow = 흐름 방향·속도(m/s, B 강은 빠르다). 조명 없는 발광 재질(블룸이 받는다)
 */
/** farGlow = 가장자리에서 먼 바다의 밝기 배율(1 = 그대로, C8-7 레이드 0.55). 가장자리 열 테(nearShore)는 줄이지 않는다 */
export function lavaSeaMaterial(shore: ProximityTex, flow: readonly [number, number], farGlow = 1): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial();
  const sUV = vec2(positionWorld.x.sub(shore.originX).div(shore.width), positionWorld.z.sub(shore.originZ).div(shore.height));
  const d = texture(shore.tex, sUV).r as N;
  const wp = positionWorld.xz.add(vec2(time.mul(flow[0]), time.mul(flow[1])));
  // 큰 흐름: 껍질 판이 모였다 흩어지는 저주파 잡음(판이 덮인 곳과 녹아 흐르는 강이 크게 갈린다)
  const warp = vec2(mx_noise_float(vec3(wp.mul(0.05), 1.1)), mx_noise_float(vec3(wp.mul(0.05), 7.3))).mul(6.0);
  const q = wp.add(warp);
  const big = mx_noise_float(vec3(q.mul(0.03), time.mul(0.015))).mul(0.5).add(0.5);
  // 녹은 바탕 위에 검은 껍질 섬이 떠다닌다(콘셉트: 밝은 용암 바다 + 검붉은 껍질 조각). 껍질 덩이는 비틀린 잡음 등고선
  const n1 = mx_noise_float(vec3(q.mul(0.09), 3.3)).mul(0.5).add(0.5);
  const n2 = mx_noise_float(vec3(q.mul(0.31), time.mul(0.03))).mul(0.5).add(0.5);
  const crustMask = smoothstep(0.5, 0.56, n1.mul(0.7).add(n2.mul(0.3)).add(big.sub(0.5).mul(0.35)));
  // 껍질 안 금(가늘게 빛난다)
  const c1 = mx_worley_noise_vec2(q.mul(0.35));
  const crack = float(1).sub(smoothstep(0.02, 0.07, c1.y.sub(c1.x)));
  const grain = mx_noise_float(vec3(q.mul(0.9), 2.0)).mul(0.5).add(0.5);
  const fine = mx_noise_float(vec3(q.mul(2.2).add(vec2(time.mul(0.15), 0)), 5.0)).mul(0.5).add(0.5);
  // 바닥 가까이(절벽 밑)는 열이 올라와 껍질이 녹는다
  const nearShore = float(1).sub(smoothstep(0.15, 0.6, d));
  const crust = mix(vec3(0.05, 0.022, 0.018), vec3(0.2, 0.07, 0.03), grain).add(vec3(1.2, 0.2, 0.02).mul(crack.mul(0.6)));
  // 녹은 바탕: 주황 ~ 노란 흐름 줄(빠른 잔물결), 멀수록 덜 밝다(블룸 과다 방지)
  const flowLines = smoothstep(0.55, 0.85, fine);
  const hotCol = mix(vec3(1.1, 0.2, 0.02), vec3(2.1, 0.85, 0.14), flowLines.mul(0.7).add(nearShore.mul(0.3)).clamp(0, 1));
  const cover = crustMask.mul(float(1).sub(nearShore.mul(0.6)));
  const col = mix(hotCol, crust, cover);
  // 판 밖(여백 260m)은 거리 지도 가장자리 값이 늘어나 '가장자리'로 읽힌다 → 판 밖은 먼 바다로 친다(C8-7 실측: 먼 바다가 안 어두워짐)
  const inGrid = float(1).sub(smoothstep(0.48, 0.5, max(sUV.x.sub(0.5).abs(), sUV.y.sub(0.5).abs())));
  m.colorNode = farGlow === 1 ? col : col.mul(mix(float(farGlow), float(1), nearShore.mul(inGrid)));
  return m;
}

/** 용암 폭포: 위가 좁고 아래가 넓은 띠에 세로로 흘러내리는 밝은 줄. 아래 끝은 붉은 김으로 흐려진다 */
export function lavafallMaterial(): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide });
  const p = uv();
  const streak = mx_noise_float(vec3(p.x.mul(10), p.y.mul(2.6).sub(time.mul(0.55)), 1.7)).mul(0.5).add(0.5);
  const streak2 = mx_noise_float(vec3(p.x.mul(23), p.y.mul(5).sub(time.mul(1.1)), 6.3)).mul(0.5).add(0.5);
  const edge = float(1).sub(smoothstep(0.3, 0.5, abs(p.x.sub(0.5))));
  const body = smoothstep(0.35, 0.6, streak.mul(0.6).add(streak2.mul(0.5)));
  const a = edge.mul(smoothstep(0.0, 0.02, p.y)).mul(body.mul(0.7).add(0.35)).mul(float(1).sub(smoothstep(0.85, 1.0, p.y).mul(0.6))).clamp(0, 0.95);
  m.colorNode = mix(vec3(1.4, 0.2, 0.02), vec3(2.6, 1.1, 0.2), streak.mul(body));
  m.opacityNode = a;
  return m;
}
